// src/components/Checkout.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import mixpanel from "mixpanel-browser";
import HelpFeedback from "./HelpFeedback"; // Import the HelpFeedback component
import { loadStripe } from '@stripe/stripe-js';
import { getPrettyDateTime } from "../utils";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import { setUserIdProperty } from "../utils/tracking";
import { getOrCreateAnonId, captureFbcId, getFbcId, getFbpId } from "../utils/sourceTracking";
import LoadingState from "./LoadingState";


// Initialize Stripe
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const stripePromise = loadStripe('pk_live_51Qsmy4KagLqHodRBESIYTPNbQ1t22riGuVU3HbpombwaORQGMWoYHqdCNwTVAAihVuMyeH54ESY6zktofizRbyNy00OSenSEBN');

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({ publicKey: currentKey });

// Helper function to ensure HTTPS URLs
// Helper function to ensure HTTPS URLs
const getSecureBaseUrl = () => {
  // Always use HTTPS for production (never HTTP except localhost)
  const protocol = window.location.hostname === 'localhost' ? window.location.protocol : 'https:';
  return `${protocol}//${window.location.host}`;
};

const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Initialize FBC capture when component loads
captureFbcId();

const Checkout = () => {
  const navigate = useNavigate();

  // Prevent search engines from indexing this member-only page
  useNoIndex();

  // Force HTTPS redirect for live Stripe keys
  useEffect(() => {
    if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
      window.location.href = window.location.href.replace('http://', 'https://');
      return;
    }
  }, []);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true); // Add auth verification state
  const userSource = localStorage.getItem("user_source"); // "meetup", "eventbrite" or null
  // NEW: State for selected slot from localStorage
  const [selectedSlot, setSelectedSlot] = useState(null);
  // COMMENTED OUT: Toggle state for ticket vs subscription (no longer needed)
  // const [selectedOption, setSelectedOption] = useState("1 ticket");

  const [selectedOption, setSelectedOption] = useState("subscription");

  // Referral code state
  const [referralCode, setReferralCode] = useState("");
  const [referralValidation, setReferralValidation] = useState({
    isValidating: false,
    isValid: false,
    error: "",
    referrerName: ""
  });
  const [showReferralInput, setShowReferralInput] = useState(false); // Collapsed by default


  // 2. PLAN DEFINITIONS
  const defaultPlan = { months: 1, pricePerMonth: 2.99, priceId: "prc_okgather-membership-xrgk0o18", label: "£2.99/month" };

  // Resolved once, up front: which landing page (if any) sent this visitor
  // here. URL param first, sessionStorage as a same-tab fallback for the
  // (rare) case the URL param gets lost along the way.
  const [initialLandingPageSlug] = useState(
    () => new URLSearchParams(window.location.search).get('lp') || sessionStorage.getItem('landingPageSlug')
  );

  // State for which set of plans to display. Starts on the site default;
  // the effect below upgrades it to a landing page's custom price once
  // (if) that's resolved from the backend. isPriceResolving gates the
  // price UI so a visitor with a landing-page slug never sees the default
  // price flash before the real one loads in.
  const [plans, setPlans] = useState([defaultPlan]);
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const [isPriceResolving, setIsPriceResolving] = useState(!!initialLandingPageSlug);
  const [showFreeTrial] = useState(false);

  // Only the landing page's *slug* is trusted from the client - the actual
  // price is always re-fetched fresh from the backend here, never read
  // from a previously-cached number. This matters because: (1) a cached
  // price can go stale if an admin changes it after the visitor's first
  // page load, (2) sessionStorage can be lost between the landing page and
  // here (ad blockers, incognito storage limits, a new tab) and silently
  // falling back to the default price would be a silent undercharge/
  // overcharge with no visible error, and (3) trusting a client-supplied
  // price number directly would let anyone override it via devtools -
  // resolving from the slug means they can only ever land on a price
  // that's actually configured for a real landing page.
  useEffect(() => {
    if (!initialLandingPageSlug) return;

    fetch(`/api/theme-landing-pages/${encodeURIComponent(initialLandingPageSlug)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data || !data.checkout_price_id) {
          return;
        }
        // checkout_price_per_month is a legacy, no-longer-editable field
        // (see AdminThemeLandingPages.js) - some landing pages still have
        // one stored, but it's never required. When it's missing, we have
        // no verified amount to show, so pricePerMonth is left null rather
        // than guessing - the price box below is simply not rendered in
        // that case, and Memberstack's own checkout still shows the real
        // amount on the next step regardless.
        const customPlan = {
          months: 1,
          pricePerMonth: data.checkout_price_per_month ?? null,
          priceId: data.checkout_price_id,
          label: data.checkout_price_per_month != null
            ? `£${Number(data.checkout_price_per_month).toFixed(2)}/month`
            : 'Continue',
        };
        setPlans([customPlan]);
        setSelectedPlan(customPlan);
      })
      .catch((err) => {
        console.error('Checkout › failed to resolve landing page price, using default:', err);
      })
      .finally(() => setIsPriceResolving(false));
  }, [initialLandingPageSlug]);

  // 3. ON MOUNT: Validate auth and decide whether to show free trial or paid plans
  // NOTE: Meta ad users are treated the same as all other users - no special checkout flow
  useEffect(() => {
    async function determinePlanAvailability() {
      const token = localStorage.getItem("memberstackToken");

      // First, validate session before making any other calls
      if (!token) {
        setShowLoginModal(true);
        setIsVerifyingAuth(false);
        return;
      }

      // A token in localStorage means this browser already completed a real
      // login/signup, so a single transient failure here (network blip, SDK
      // not fully settled right after a redirect) shouldn't immediately be
      // treated as "logged out" - retry once before giving up.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const currentMember = await memberstack.getCurrentMember();

          if (currentMember && currentMember.data) {
            // Auth verified — plan/price resolution is handled by the
            // effect above, independently of auth state.
            setIsVerifyingAuth(false);
            return;
          }
        } catch (err) {
          console.error("Checkout › determinePlanAvailability error:", err);
        }

        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      }

      setShowLoginModal(true);
      setIsVerifyingAuth(false);
    }
    determinePlanAvailability();

    // Track form view event using guarded call
    const trackFormView = async () => {
      try {
        // Critical: Set user ID in background, don't wait for it
        setUserIdProperty(); // No await - completely non-blocking

        // Check if user arrived via referral
        const hasReferralCode = localStorage.getItem('referralCode') ||
                                new URLSearchParams(window.location.search).get('ref');

        // Track immediately, don't wait for user ID
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("Checkout: Form Viewed", {
            database_user_id: window.okgatherUserId || null,
            has_referral_code: !!hasReferralCode,
            referral_code: hasReferralCode ? hasReferralCode.toUpperCase() : null
          });

          // Track as funnel step if referral is present
          if (hasReferralCode) {
            mixpanel.track("Referral Funnel: Checkout Viewed", {
              funnel_step: 'checkout',
              funnel_step_number: 5,
              referral_code: hasReferralCode.toUpperCase(),
              has_referral_code: true
            });
          }
        }
      } catch (e) {
        // Silent failure - checkout must never be impacted
      }
    };
    trackFormView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberstack]);

  // Extract ncc parameter from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const nccParam = urlParams.get('ncc');
    const campaignParam = urlParams.get('campaign');

    // Store ncc flag in localStorage if present
    if (nccParam === 'true') {
      localStorage.setItem('ncc', 'true');
      if (campaignParam) {
        localStorage.setItem('ncc_campaign', campaignParam);
      }
    }
  }, []);

  // Extract referral code from URL or localStorage on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let refCode = urlParams.get('ref');

    // If no URL parameter, check localStorage
    if (!refCode) {
      refCode = localStorage.getItem('referralCode');
    }

    if (refCode) {
      const upperCode = refCode.toUpperCase().trim();
      setReferralCode(upperCode);
      // Auto-validate if code from URL or localStorage (silent - no input shown)
      validateReferralCode(upperCode);
      // Don't show input field - just auto-apply

      // Store in localStorage so it persists across page refreshes
      // Will be cleared after successful checkout on the success page
      localStorage.setItem('referralCode', upperCode);
    }
  }, []);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.trim().length === 0) {
      setReferralValidation({
        isValidating: false,
        isValid: false,
        error: "",
        referrerName: ""
      });
      return;
    }

    setReferralValidation({
      isValidating: true,
      isValid: false,
      error: "",
      referrerName: ""
    });

    try {
      const response = await fetch('/api/referral/validate-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ referralCode: code.toUpperCase().trim() })
      });

      const data = await response.json();

      if (data.valid) {
        setReferralValidation({
          isValidating: false,
          isValid: true,
          error: "",
          referrerName: data.referrerName || "Friend"
        });

        // Track successful referral code entry
        if (mixpanel && mixpanel.__loaded) {
          const source = window.location.search.includes('ref=') ? 'url' :
                        localStorage.getItem('referralCode') ? 'referral_landing' : 'manual';

          mixpanel.track("Referral Code Applied", {
            code: code.toUpperCase(),
            source: source,
            referrer_name: data.referrerName
          });

          // Track as funnel step
          mixpanel.track("Referral Funnel: Code Applied at Checkout", {
            funnel_step: 'checkout',
            funnel_step_number: 5,
            referral_code: code.toUpperCase(),
            source: source,
            referrer_name: data.referrerName
          });
        }
      } else {
        setReferralValidation({
          isValidating: false,
          isValid: false,
          error: data.error || "Invalid referral code",
          referrerName: ""
        });
      }
    } catch (error) {
      console.error("Error validating referral code:", error);
      setReferralValidation({
        isValidating: false,
        isValid: false,
        error: "Unable to validate code. Please try again.",
        referrerName: ""
      });
    }
  };

  // Handle referral code input change with debounce
  const handleReferralCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setReferralCode(value);

    // Clear validation while typing
    if (value.length === 0) {
      setReferralValidation({
        isValidating: false,
        isValid: false,
        error: "",
        referrerName: ""
      });
    }
  };

  // Handle referral code blur (validate on blur)
  const handleReferralCodeBlur = () => {
    if (referralCode.trim().length > 0) {
      validateReferralCode(referralCode);
    }
  };

  // NEW: Retrieve selected slot data from localStorage
  useEffect(() => {
    const storedSlotData = localStorage.getItem("selectedSlotData");
    if (storedSlotData) {
      try {
        const slotData = JSON.parse(storedSlotData);
        setSelectedSlot(slotData);
      } catch (error) {
        console.error("Error parsing stored slot data:", error);
      }
    }
  }, []);


  // Session validation is now handled in the determinePlanAvailability useEffect above

  // 4. HANDLE PLAN SELECTION
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track("Checkout: Plan Selected", {
        plan,
        database_user_id: window.okgatherUserId || null
      });
    }
  };

  // 5. HANDLE CHECKOUT CLICK

  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const hasTrackedCheckout = useRef(false);
  const handleCheckout = async () => {
    if (isCheckingOut) return;

    if (hasTrackedCheckout.current) {
      console.log('Already tracked checkout, skipping duplicate tracking');
      return;
    }
    hasTrackedCheckout.current = true;
    setIsCheckingOut(true);

    try {
      // Ensure we're on HTTPS before proceeding with Stripe
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        window.location.href = window.location.href.replace('http://', 'https://');
        return;
      }

      if (mixpanel && mixpanel.__loaded) {
        const hasReferralCode = referralValidation.isValid && referralCode;

        mixpanel.track("Checkout: Initiated", {
          option: selectedOption,
          plan: selectedOption === "subscription" ? selectedPlan : null,
          database_user_id: window.okgatherUserId || null,
          has_referral_code: hasReferralCode,
          referral_code: hasReferralCode ? referralCode : null
        });

        // Track referral funnel completion
        if (hasReferralCode) {
          mixpanel.track("Referral Funnel: Checkout Initiated", {
            funnel_step: 'checkout_initiated',
            funnel_step_number: 6,
            referral_code: referralCode,
            plan_selected: selectedPlan.months,
            plan_value: showFreeTrial || selectedPlan.pricePerMonth == null
              ? 0
              : selectedPlan.pricePerMonth * selectedPlan.months
          });
        }
      }

      const eventId = generateEventId();
      // Analytics/ad-attribution value only (Meta pixel, backend tracking) -
      // never shown to the customer. Falls back to the site default price
      // when this plan has no verified amount (a custom Price ID with no
      // legacy price_per_month stored), since an approximate conversion
      // value here is preferable to sending NaN to ad platforms.
      const pricePerMonthForTracking = selectedPlan.pricePerMonth ?? defaultPlan.pricePerMonth;
      const value = (pricePerMonthForTracking * (selectedPlan.months || 1)).toFixed(2);

      // Store comprehensive checkout data for success page
      sessionStorage.setItem('checkoutData', JSON.stringify({
        eventId,
        priceId: selectedPlan.priceId,
        value: value,
        currency: 'GBP',
        timestamp: Date.now(),
        isFreeTrial: showFreeTrial
      }));

      // Fire Meta pixel InitiateCheckout
      // IMPORTANT: eventID must be in 4th parameter (options) for deduplication
      window.fbq?.('track', 'InitiateCheckout', {
        value: value,
        currency: "GBP"
      }, {
        eventID: eventId
      });

      // Send tracking data to backend (non-critical)
      try {
        const token = localStorage.getItem("memberstackToken");
        const fbcId = getFbcId();
        const fbpId = getFbpId();

        if (token) {
          const trackingData = {
            event_id: eventId,
            value: parseFloat(value),
            currency: "GBP"
          };

          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };

          if (fbcId) {
            headers['X-Fbc'] = fbcId;
          }
          if (fbpId) {
            headers['X-Fbp'] = fbpId;
          }

          // Send to backend without blocking checkout flow
          fetch('/api/tracking/initiate-checkout', {
            method: 'POST',
            headers,
            body: JSON.stringify(trackingData)
          }).catch(err => console.warn('Backend tracking failed:', err));
        }
      } catch (trackingError) {
        console.warn('Tracking setup failed:', trackingError);
      }

      const baseUrl = getSecureBaseUrl();
      const successUrl = `${baseUrl}/my-events?purchase=1&value=${value}&event_id=${eventId}`;
      const cancelUrl = `${baseUrl}/checkout`;

      // Store referral code in localStorage before checkout (if valid)
      // The backend webhook will pick it up from the user record
      if (showFreeTrial && referralValidation.isValid && referralCode) {
        try {
          const token = localStorage.getItem("memberstackToken");
          if (token) {
            // Store referral code on user record before checkout
            await fetch('/api/user/set-referral-code', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                referralCode: referralCode
              })
            }).catch(err => console.warn('Failed to store referral code:', err));
          }
        } catch (err) {
          console.warn('Error storing referral code:', err);
        }
      }

      // Attribute this signup to an invite link if this visitor arrived
      // via one (see GatheringInviteLink's docstring in models.py, and the
      // capture in ThemeLandingPage.js). Silently no-ops on an
      // invalid/expired token server-side, and never blocks checkout.
      const inviteToken = localStorage.getItem('inviteToken');
      if (inviteToken) {
        try {
          const token = localStorage.getItem("memberstackToken");
          if (token) {
            await fetch('/api/user/attach-invite-token', {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ token: inviteToken })
            }).catch(err => console.warn('Failed to attach invite token:', err));
            localStorage.removeItem('inviteToken');
          }
        } catch (err) {
          console.warn('Error attaching invite token:', err);
        }
      }

      // Check for no-credit-card (NCC) trial flow
      const nccFlag = localStorage.getItem('ncc');
      const nccCampaign = localStorage.getItem('ncc_campaign') || 'unknown';
      const token = localStorage.getItem("memberstackToken");

      if (nccFlag === 'true' && showFreeTrial && token) {
        try {
          // Check eligibility with backend
          const eligibilityResponse = await fetch('/api/user/check-ncc-eligibility', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const eligibilityData = await eligibilityResponse.json();

          if (eligibilityData.eligible) {
            // Add user to FREE Memberstack plan (30-day trial without CC)
            const FREE_TRIAL_PLAN_ID = 'pln_free-plan-kwo20e9r';

            await memberstack.addPlan({ planId: FREE_TRIAL_PLAN_ID });

            // Track NCC trial start in backend
            await fetch('/api/user/start-ncc-trial', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ campaign: nccCampaign })
            });

            // Clear localStorage to prevent reuse
            localStorage.removeItem('ncc');
            localStorage.removeItem('ncc_campaign');

            // Track in Mixpanel
            if (mixpanel && mixpanel.__loaded) {
              mixpanel.track("Checkout: NCC Trial Started", {
                campaign: nccCampaign,
                database_user_id: window.okgatherUserId || null
              });
            }

            // Redirect to my-events
            navigate('/my-events');
            return;
          } else {
            console.log('User not eligible for NCC trial, falling back to standard checkout');
          }
        } catch (nccError) {
          console.error('NCC trial flow failed, falling back to standard checkout:', nccError);
        }
      }

      // Attach acquisition source (e.g. Meetup/Eventbrite group) to Stripe metadata
      const sourceGroup = localStorage.getItem('source_group');
      const explicitPlatform = localStorage.getItem('source_platform');
      const placement = localStorage.getItem('source_placement');
      // First-touch, localStorage-backed (see sourceTracking.js/tracking.js) -
      // durable proof of which landing page/variant this subscription came
      // from, read straight back out of Stripe metadata by the admin
      // Landing Page Performance report instead of relying on a Mixpanel
      // identity merge that doesn't reliably happen (see
      // mixpanel_service.py's _count_subscriptions_by_landing_page).
      const landingPage = localStorage.getItem('landing_page');
      const landingPageVariant = localStorage.getItem('landing_page_variant');
      // Distinct from landingPage above: only set if the visitor actually
      // clicked a CTA inside a blog post (see recordBlogCtaClick in
      // sourceTracking.js), not just viewed one - lets reporting tell "read
      // a blog post, subscribed some other way" apart from "clicked through
      // from a blog post and subscribed".
      const blogCtaClicked = localStorage.getItem('blog_cta_clicked');
      // Independent of landingPage above: landingPage gets overwritten by
      // recordThemeSelection() the moment a visitor picks a theme on
      // /events - which is exactly where every blog CTA sends them - so
      // this is the durable "did they view a blog post at some point"
      // signal that survives that overwrite (see captureFirstBlogPostViewed
      // in sourceTracking.js).
      const firstBlogPostViewed = localStorage.getItem('first_blog_post_viewed');
      const metadataForCheckout = {};
      if (userSource) metadataForCheckout.traffic_source = userSource;
      // Prefer an explicit ?platform= param; fall back to referrer-based detection
      if (explicitPlatform || userSource) metadataForCheckout.platform = explicitPlatform || userSource;
      if (sourceGroup) metadataForCheckout.source_group = sourceGroup;
      if (placement) metadataForCheckout.placement = placement;
      if (landingPage) metadataForCheckout.landing_page = landingPage;
      if (landingPageVariant) metadataForCheckout.landing_page_variant = landingPageVariant;
      if (blogCtaClicked) metadataForCheckout.blog_cta_clicked = blogCtaClicked;
      if (firstBlogPostViewed) metadataForCheckout.first_blog_post_viewed = firstBlogPostViewed;
      // Lets the backend fall back to the most recent page_views row for
      // this browser when landing_page above is missing (cleared storage,
      // a different device/tab) - see models.PageView's docstring and
      // mixpanel_service's page_views-by-anon_id attribution fallback.
      metadataForCheckout.anon_id = getOrCreateAnonId();

      // Use Memberstack flow for all subscriptions (handles price ID conversion)
      await memberstack.purchasePlansWithCheckout({
        priceId: selectedPlan.priceId,
        successUrl,
        cancelUrl,
        autoRedirect: true,
        ...(Object.keys(metadataForCheckout).length > 0 && { metadataForCheckout }),
      });

      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("Checkout: Purchase Initiated", {
          plan: selectedPlan,
          database_user_id: window.okgatherUserId || null
        });
      }

    } catch (err) {
      setIsCheckingOut(false);
      hasTrackedCheckout.current = false;

      if (mixpanel && mixpanel.__loaded) {
        mixpanel.track("Checkout: Error", {
          error: err.message,
          option: selectedOption,
          database_user_id: window.okgatherUserId || null
        });
      }
      alert("Something went wrong. Please refresh and try again.");
    }
  };

  // Show loading state while verifying authentication
  if (isVerifyingAuth) {
    return <LoadingState />;
  }

  // 6. RENDER
  return (
    <div className="page-wrapper" style={{ position: "relative" }}>
      {/* Login Modal (only shown when user not logged-in) */}
      {showLoginModal && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "24px",
              borderRadius: "8px",
              textAlign: "center",
              maxWidth: "320px",
              width: "80%",
            }}
          >
            {localStorage.getItem("memberstackToken") ? (
              // A token already exists, so this is a member whose session
              // check failed, not a new visitor - offering "Create Account"
              // here would try to sign up their already-registered email
              // and fail with a confusing "email already in use" error.
              <>
                <p>We couldn't verify your session. Please log in again to continue.</p>
                <button
                  className="button-2 is-black w-button"
                  onClick={() => navigate("/login")}
                >
                  Log In
                </button>
              </>
            ) : (
              <>
                <p>Please create an account or log in to continue.</p>
                <button
                  className="button-2 is-black w-button"
                  style={{ marginBottom: "10px" }}
                  onClick={() => navigate("/create-account")}
                >
                  Create Account
                </button>
                <br />
                <button
                  style={{ background: "transparent", border: "none", color: "#8B5FBF", cursor: "pointer", fontSize: "0.9rem", textDecoration: "underline" }}
                  onClick={() => navigate("/login")}
                >
                  Already have an account? Log in
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large">
            <div className="container-large">
              <div
                className="hero_card is-vertical"
                style={{ position: "relative", paddingLeft: "10px", paddingRight: "10px" }}
              >
                <HelpFeedback />

                {/* Headings */}
                <h3 style={{ textAlign: "center" }}>
                  Join OkGather 🥳
                </h3>

                {/* Show subscription content directly (no conditional) */}
                <>
                  {/* Membership benefits summary */}
                  <div style={{
                    maxWidth: "420px",
                    margin: "0 auto 20px",
                    padding: "16px 20px",
                    background: "#f0fdf4",
                    borderRadius: "12px",
                    border: "1px solid rgba(16, 185, 129, 0.25)",
                  }}>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: "0.95rem", color: "#444", lineHeight: "2" }}>
                      <li>✅ &nbsp;Attend <strong>as often as you like</strong></li>
                      <li>🍹 &nbsp;<strong>20% off food and drinks</strong> at the venue - show your profile at the bar</li>
                    </ul>
                  </div>

                  {/* Location of the slot the user just picked */}
                  {selectedSlot?.locationCity && (
                    <p style={{ textAlign: "center", margin: "0 auto 16px", fontSize: "0.9rem", color: "#555" }}>
                      📍 {selectedSlot.locationCity}{selectedSlot.locationArea ? ` - ${selectedSlot.locationArea}` : ""}
                    </p>
                  )}

                  {/* Success banner when referral code is valid */}
                  {referralValidation.isValid && (
                    <div style={{
                      maxWidth: "500px",
                      margin: "16px auto",
                      padding: "12px 16px",
                      background: "#d1fae5",
                      border: "1px solid #10b981",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}>
                      <div style={{
                        fontSize: "14px",
                        color: "#065f46",
                        fontWeight: "500"
                      }}>
                        🎉 Invite code applied! You'll save £10 on your first payment
                      </div>
                    </div>
                  )}

                  <div
                    className="subscription-options"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                      marginBottom: "24px",
                    }}
                  >
                    {isPriceResolving ? (
                      <div style={{ padding: "20px 28px", textAlign: "center", color: "#555" }}>
                        Loading price...
                      </div>
                    ) : plans.map((plan) => {
                      // No verified amount for this plan (a custom Price ID
                      // with no legacy price_per_month stored) - nothing to
                      // show here that we can vouch for, so skip the price
                      // box entirely rather than display a guess. The
                      // Continue button below still selects/checks out
                      // this plan normally.
                      if (plan.pricePerMonth == null) {
                        return null;
                      }
                      const isSelected = plan.priceId === selectedPlan.priceId;
                      return (
                        <div
                          key={plan.priceId}
                          className="subscription-box"
                          onClick={() => handlePlanSelect(plan)}
                          style={{
                            border: isSelected ? "2px solid #000" : "1px solid #ccc",
                            padding: "20px 28px",
                            textAlign: "center",
                            cursor: "pointer",
                            width: "220px",
                            margin: "0 3px",
                            borderRadius: "8px",
                            background: "#f0fdf4",
                          }}
                        >
                          <div style={{ fontSize: "14px", color: "#555", marginBottom: "4px" }}>
                            only
                          </div>
                          <div style={{ fontSize: "20px", fontWeight: "bold", lineHeight: "1" }}>
                            £{plan.pricePerMonth.toFixed(2)}
                          </div>
                          <div style={{ fontSize: "14px", color: "#555", marginTop: "4px" }}>
                            per month
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Checkout Button */}
                  <button
                    className="button-2 is-black w-button"
                    style={{ display: "block", margin: "0 auto", marginBottom: "16px", opacity: isPriceResolving ? 0.6 : 1 }}
                    onClick={handleCheckout}
                    disabled={isPriceResolving}
                  >
                    Continue
                  </button>

                  {/* Collapsible Referral Code Input (only show for free trial) - Repositioned below button */}
                  {showFreeTrial && !referralValidation.isValid && (
                    <div style={{
                      textAlign: "center",
                      marginBottom: "16px"
                    }}>
                      {!showReferralInput ? (
                        // Collapsed state - just a link
                        <button
                          onClick={() => setShowReferralInput(true)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#8B5FBF",
                            fontSize: "13px",
                            cursor: "pointer",
                            textDecoration: "underline",
                            padding: "0"
                          }}
                        >
                          Have an invite code?
                        </button>
                      ) : (
                        // Expanded state - show input
                        <div style={{
                          maxWidth: "380px",
                          margin: "0 auto",
                          padding: "16px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          border: `1px solid ${referralValidation.error ? "#ef4444" : "#e5e7eb"}`,
                        }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "10px"
                          }}>
                            <label style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#374151",
                            }}>
                              Enter invite code
                            </label>
                            <button
                              onClick={() => {
                                setShowReferralInput(false);
                                setReferralCode("");
                                setReferralValidation({
                                  isValidating: false,
                                  isValid: false,
                                  error: "",
                                  referrerName: ""
                                });
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#9ca3af",
                                fontSize: "18px",
                                cursor: "pointer",
                                padding: "0",
                                lineHeight: "1"
                              }}
                            >
                              ✕
                            </button>
                          </div>

                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              value={referralCode}
                              onChange={handleReferralCodeChange}
                              onBlur={handleReferralCodeBlur}
                              placeholder="e.g., SARAH2K4L"
                              autoFocus
                              style={{
                                width: "100%",
                                padding: "10px 35px 10px 10px",
                                fontSize: "13px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                outline: "none",
                                fontFamily: "monospace",
                                letterSpacing: "1px"
                              }}
                            />

                            {/* Validation indicator */}
                            {referralValidation.isValidating && (
                              <div style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "14px"
                              }}>
                                ⏳
                              </div>
                            )}

                            {referralValidation.isValid && (
                              <div style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "14px",
                                color: "#10b981"
                              }}>
                                ✓
                              </div>
                            )}

                            {referralValidation.error && !referralValidation.isValidating && (
                              <div style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "14px",
                                color: "#ef4444"
                              }}>
                                ✗
                              </div>
                            )}
                          </div>

                          {/* Error message */}
                          {referralValidation.error && !referralValidation.isValidating && (
                            <div style={{
                              marginTop: "8px",
                              fontSize: "12px",
                              color: "#991b1b"
                            }}>
                              {referralValidation.error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Disclaimer */}
                  <p
                    style={{
                      fontSize: "10px",
                      textAlign: "center",
                      color: "#666",
                      maxWidth: "500px",
                      margin: "0 auto",
                    }}
                  >
                    {"Your subscription will auto-renew for the same length and price until you cancel. You acknowledge your right to withdraw within 14 days for a pro-rated refund. By subscribing, you agree to our Terms."}
                  </p>
                </>

              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Checkout;